import {
    AfterViewInit,
    Component,
    ElementRef,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChildren,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import { DateTime } from "luxon";
import { Chart } from "chart.js";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { SharedComponentsModule } from "@/components/shared/shared-components.module";
import { StatsService } from "@/_services";
import { TerminologyService } from "@/_services/terminology/terminology.service";
import { TermKey } from "@/_services/terminology/catalog";
import { DashboardChart } from "@/_models/dashboard-stats.interface";
import { CHART_COLOURS } from "./performance-chart.config";

/** A chart resolved for display: labels, headline and the data Chart.js takes. */
interface RenderedChart {
    key: string;
    title: string;
    caption: string;
    headline: string;
    headlineLabel: string;
    config: any;
}

/**
 * The dashboard's Performance panel (#3011).
 *
 * Two trends, chosen by the account's plan, because what the data can answer
 * differs by plan. The card links into the full Performance section rather
 * than trying to replace it.
 */
@UntilDestroy()
@Component({
    selector: "app-performance-panel",
    standalone: true,
    imports: [CommonModule, RouterModule, SharedComponentsModule],
    templateUrl: "./performance-panel.component.html",
    styleUrl: "./performance-panel.component.scss",
})
export class PerformancePanelComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    @ViewChildren("chartCanvas") canvases!: QueryList<ElementRef>;

    /**
     * Whose figures to show. A member gets their own from an endpoint scoped
     * to them, so the account-wide payload never reaches their page at all.
     */
    @Input() scope: "account" | "mine" = "account";

    /**
     * Stacked in the member's right column rather than spread full width.
     *
     * Also drops the panel's own top margin: in the column the spacing comes
     * from the column's gap, and adding to it set this panel further from what
     * precedes it than Program Health sits from Ask RISE.
     */
    @HostBinding("class.compact")
    @Input()
    compact = false;

    title = "";
    linkLabel = "";
    charts: RenderedChart[] = [];
    loading = true;

    private drawn: Chart[] = [];
    private viewReady = false;

    constructor(
        private statsService: StatsService,
        private terminology: TerminologyService,
    ) {}

    ngOnInit(): void {
        this.title = this.term(
            this.scope === "mine"
                ? "dashboard.performance.mineTitle"
                : "dashboard.performance.title",
        );
        this.linkLabel = this.term("dashboard.performance.link");

        const figures$ =
            this.scope === "mine"
                ? this.statsService.getMemberDashboardStats()
                : this.statsService.getDashboardStats();

        figures$.pipe(untilDestroyed(this)).subscribe({
            next: (stats) => {
                this.charts = (stats.performance ?? []).map((chart) =>
                    this.render(chart),
                );
                this.loading = false;
                this.draw();
            },
            error: () => {
                this.loading = false;
            },
        });
    }

    ngAfterViewInit(): void {
        this.viewReady = true;
        this.canvases.changes
            .pipe(untilDestroyed(this))
            .subscribe(() => this.draw());
        this.draw();
    }

    ngOnDestroy(): void {
        this.destroyCharts();
    }

    /**
     * Draws once the data and the canvases are both there, in whichever order
     * they arrive, and tears down the previous charts first: Chart.js keeps a
     * registry per canvas and leaks the old instance otherwise.
     */
    private draw(): void {
        if (!this.viewReady || !this.charts.length) {
            return;
        }

        setTimeout(() => {
            this.destroyCharts();
            this.canvases.forEach((canvas, index) => {
                const chart = this.charts[index];
                if (chart) {
                    this.drawn.push(
                        new Chart(canvas.nativeElement, chart.config),
                    );
                }
            });
        });
    }

    private destroyCharts(): void {
        this.drawn.forEach((chart) => chart.destroy());
        this.drawn = [];
    }

    private render(chart: DashboardChart): RenderedChart {
        const labels = chart.labels.map((month) =>
            DateTime.fromFormat(month, "yyyy-MM").toFormat("LLL"),
        );

        return {
            key: chart.key,
            title: this.term(`dashboard.performance.${chart.key}.title`),
            caption: this.term(`dashboard.performance.${chart.key}.caption`),
            headline: this.formatHeadline(chart),
            headlineLabel: this.term(
                `dashboard.performance.${chart.key}.headline`,
            ),
            config: {
                type: chart.kind,
                data: {
                    labels,
                    datasets: chart.series.map((series, index) => ({
                        label: this.term(
                            `dashboard.performance.series.${series.key}`,
                        ),
                        data: series.values,
                        ...CHART_COLOURS[index % CHART_COLOURS.length],
                        ...(chart.kind === "line"
                            ? { fill: false, lineTension: 0.3 }
                            : {}),
                    })),
                },
                options: this.chartOptions(chart),
            },
        };
    }

    /**
     * Axes and gridlines are on: a trend without them is a shape rather than a
     * measurement, and this panel exists to be read off.
     */
    private chartOptions(chart: DashboardChart): any {
        const isPercent = chart.headline.format === "percent";
        return {
            maintainAspectRatio: false,
            responsive: true,
            plugins: { datalabels: { display: false } },
            legend: {
                display: chart.series.length > 1,
                position: "bottom",
                align: "start",
                labels: { boxWidth: 12, usePointStyle: true },
            },
            tooltips: { intersect: false, mode: "index" },
            scales: {
                yAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                            ...(isPercent ? { max: 100 } : {}),
                            precision: 0,
                        },
                        gridLines: { drawBorder: false },
                    },
                ],
                xAxes: [{ gridLines: { display: false } }],
            },
        };
    }

    private formatHeadline(chart: DashboardChart): string {
        const { value, format } = chart.headline;
        if (format === "percent") {
            return `${value}%`;
        }
        if (format === "signed") {
            return value > 0 ? `+${value}` : String(value);
        }
        return String(value);
    }

    private term(key: string): string {
        return this.terminology.resolve(key as TermKey);
    }
}
